import { useState, useEffect, useCallback, type MouseEvent, type SyntheticEvent } from 'react'
import { Slide } from '@mui/material'
import { useSwipeable, type SwipeEventData } from 'react-swipeable'
import { Alert } from '../../primitives/Alert'
import { Box } from '../../primitives/Box'
import { type Toast } from './types'
import { useSnackbarContext } from './use-snackbar-context'
import { getDirection } from './get-direction'

type Props = {
  toast: Toast
}

const swipeDismissDistance = 20

export const SnackbarToast = ({ toast }: Props) => {
  const [open, setOpen] = useState(toast.visible)
  const { removeToast } = useSnackbarContext()

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => {
        close()
      }, toast.duration || 6000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [close, toast.duration])

  useEffect(() => {
    if (!toast.visible) {
      close()
    }
  }, [toast.visible, close])

  const handleAnimationExit = () => removeToast(toast.key)

  const handleClose = (event: SyntheticEvent) => {
    toast.onClose?.(event)
    close()
  }

  const swipeHandlers = useSwipeable({
    trackMouse: true,
    delta: swipeDismissDistance,
    onSwiping: ({ deltaX, dir }: SwipeEventData) => {
      if ((dir === 'Left' || dir === 'Right') && Math.abs(deltaX) > swipeDismissDistance) {
        close()
      }
    },
  })

  return (
    <Slide
      in={open}
      timeout={toast.transitionDuration}
      onExited={handleAnimationExit}
      direction={getDirection(toast.position)}
    >
      <Box key={toast.key}>
        <Box
          {...swipeHandlers}
          data-testid="snackbar-message"
          onClickCapture={(event: MouseEvent<HTMLDivElement>) => {
            if (!open) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          onClick={close}
          sx={{ touchAction: 'pan-y' }}
        >
          <Alert
            severity={toast.severity}
            variant={toast.variant}
            title={toast.title}
            action={toast.action}
            onClose={handleClose}
          >
            {toast.message}
          </Alert>
        </Box>
      </Box>
    </Slide>
  )
}
